/**
 * VerifyEmailPage
 *
 * Handles two scenarios:
 *  1. /auth/verify-email?token=XXX  — verifies the token from the email link
 *  2. /auth/verify-email-sent       — confirmation screen shown after registration
 *
 * The token verification is fire-once on mount (no retry needed — token is consumed).
 */

import { useState, useEffect, memo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiVerifyEmail, apiResendVerification } from '../api/auth';
import { ApiError } from '../api/client';

// ── Verify token from URL ─────────────────────────────────────────────────────

type VerifyPhase = 'verifying' | 'success' | 'error';

const VerifyTokenView = memo(function VerifyTokenView({ token }: { token: string }) {
  const [phase,   setPhase]   = useState<VerifyPhase>('verifying');
  const [message, setMessage] = useState('');

  useEffect(() => {
    apiVerifyEmail(token)
      .then((res) => {
        setMessage(res.message);
        setPhase('success');
      })
      .catch((err: unknown) => {
        setMessage(
          err instanceof ApiError
            ? err.message
            : 'Verification failed — the link may have expired.',
        );
        setPhase('error');
      });
  }, [token]);

  return (
    <div className="auth-page">
      <div className="auth-header">
        <h1 className="auth-title">email verification</h1>
      </div>

      {phase === 'verifying' && (
        <div className="tfa-loading">
          <span className="auth-spinner tfa-loading-spinner" />
          Verifying your email…
        </div>
      )}

      {phase === 'success' && (
        <>
          <div className="tfa-success-banner" role="status">
            <span className="tfa-success-icon">✓</span>
            {message}
          </div>
          <Link to="/login" className="btn btn--primary auth-submit-btn">
            sign in
          </Link>
        </>
      )}

      {phase === 'error' && (
        <>
          <div className="form-server-error" role="alert">{message}</div>
          <p className="auth-footer">
            <Link to="/auth/resend-verification">Request a new verification link →</Link>
          </p>
        </>
      )}
    </div>
  );
});

// ── "Check your email" screen (after registration) ────────────────────────────

const VerifyEmailSentView = memo(function VerifyEmailSentView() {
  return (
    <div className="auth-page">
      <div className="auth-header">
        <h1 className="auth-title">check your email</h1>
        <p className="auth-subtitle">
          We sent a verification link to your address.
        </p>
      </div>

      <div className="verify-email-info">
        <p className="verify-email-body">
          Click the link in the email to verify your account and unlock sign in.
          The link expires in <strong>24 hours</strong>.
        </p>
        <p className="verify-email-note">
          Didn't get it? Check your spam folder, or{' '}
          <Link to="/auth/resend-verification">request a new link</Link>.
        </p>
      </div>

      <p className="auth-footer">
        <Link to="/login">← back to sign in</Link>
      </p>
    </div>
  );
});

// ── Resend form ───────────────────────────────────────────────────────────────

const ResendVerificationView = memo(function ResendVerificationView() {
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    apiResendVerification({ email })
      .then(() => setSent(true))
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError && err.status === 429
            ? 'Too many requests — please wait a moment'
            : 'Something went wrong — please try again',
        );
      })
      .finally(() => setLoading(false));
  }

  return (
    <div className="auth-page">
      <div className="auth-header">
        <h1 className="auth-title">resend verification</h1>
        <p className="auth-subtitle">Enter your email to receive a new link.</p>
      </div>

      {sent ? (
        <>
          <div className="tfa-success-banner" role="status">
            <span className="tfa-success-icon">✓</span>
            If that email has an unverified account, a new link has been sent.
          </div>
          <p className="auth-footer">
            <Link to="/login">← back to sign in</Link>
          </p>
        </>
      ) : (
        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="form-field">
            <label className="form-label" htmlFor="resend-email">email</label>
            <input
              id="resend-email"
              className="form-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>
          {error && <div className="form-server-error" role="alert">{error}</div>}
          <button
            className="btn btn--primary auth-submit-btn"
            type="submit"
            disabled={loading || !email}
          >
            {loading ? (
              <span className="auth-btn-loading">
                <span className="auth-spinner" />
                sending…
              </span>
            ) : (
              'send link'
            )}
          </button>
        </form>
      )}
    </div>
  );
});

// ── Page root: routes to correct sub-view ─────────────────────────────────────

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  if (token) return <VerifyTokenView token={token} />;
  return <VerifyEmailSentView />;
}

export { ResendVerificationView as ResendVerificationPage };
